<template>
  <div class="pa-root">
    <div class="pa-loading" v-if="isLoading">
      Loading...
    </div>

    <div class="pa-empty" v-else-if="hasError">
      {{ errorMsg || "Failed to load" }}
    </div>

    <div class="pa-grid" v-else>
      <div class="pa-card pa-card--borrower">
        <div class="pa-card__top">
          <div class="pa-card__title">
            <i class="fa-duotone fa-user-check"></i>
            Borrower Tasks
          </div>
        </div>

        <div class="pa-card__metrics pa-card__metrics--two">
          <button class="pa-metric pa-metric--click" type="button" @click.stop="openBorrowerTasks()">
            <div class="pa-metric__label">Pending borrower tasks</div>
            <div class="pa-metric__value">{{ borrowerPendingCount }}</div>
          </button>

          <button class="pa-metric pa-metric--click" type="button" @click.stop="openRequestedBorrowerTasks()">
            <div class="pa-metric__label">Requested borrower tasks</div>
            <div class="pa-metric__value">{{ borrowerRequestedCount }}</div>
          </button>
        </div>

        <div class="pa-card__footer">
          <span class="pa-card__footer-left">
            <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            View details
          </span>
          <span class="pa-card__footer-right">
            Updated {{ lastUpdatedLabel }}
          </span>
        </div>
      </div>

      <div class="pa-card pa-card--loanflow">
        <div class="pa-card__top">
          <div class="pa-card__title">
            <i class="fa-duotone fa-briefcase"></i>
            LoanFlow
          </div>
        </div>

        <div class="pa-card__metrics pa-card__metrics--three">
          <button class="pa-metric pa-metric--click" type="button" @click.stop="openDueToday()">
            <div class="pa-metric__label">Due Today</div>
            <div class="pa-metric__value">{{ dueTodayCount }}</div>
          </button>

          <button class="pa-metric pa-metric--click" type="button" @click.stop="openPastDue()">
            <div class="pa-metric__label">Past Due</div>
            <div class="pa-metric__value">{{ pastDueCount }}</div>
          </button>

          <button class="pa-metric pa-metric--click" type="button" @click.stop="openOpen()">
            <div class="pa-metric__label">Open</div>
            <div class="pa-metric__value">{{ openCount }}</div>
          </button>
        </div>

        <div class="pa-card__footer">
          <span class="pa-card__footer-left">
            <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            View details
          </span>
          <span class="pa-card__footer-right">
            Updated {{ lastUpdatedLabel }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "@/api/api.service.js"
import { isToday, isTaskPastDue } from "@/helpers/formatters.js"

export default {
  name: "ProcessorActionRequiredSummary",
  props: {
    azureId: { type: String, required: true },
    accessToken: { type: String, required: true },
    taskSwitchChecked: { type: Boolean, default: false },
    openTaskColumns: { type: Array, required: true },
    internalTaskColumns: { type: Array, required: true }
  },
  emits: ["open-grid"],
  data() {
    return {
      isLoading: false,
      hasError: false,
      errorMsg: "",
      lastUpdatedAt: null,

      borrowerPending: [],
      borrowerRequested: [],
      loanDueToday: [],
      loanPastDue: [],
      loanOpen: [],

      nowTick: Date.now(),
      _nowTimer: null,
      _reqId: 0
    }
  },
  computed: {
    borrowerPendingCount() {
      return Array.isArray(this.borrowerPending) ? this.borrowerPending.length : 0
    },
    borrowerRequestedCount() {
      return Array.isArray(this.borrowerRequested) ? this.borrowerRequested.length : 0
    },
    dueTodayCount() {
      return Array.isArray(this.loanDueToday) ? this.loanDueToday.length : 0
    },
    pastDueCount() {
      return Array.isArray(this.loanPastDue) ? this.loanPastDue.length : 0
    },
    openCount() {
      return Array.isArray(this.loanOpen) ? this.loanOpen.length : 0
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return "—"

      const updated = new Date(this.lastUpdatedAt).getTime()
      if (Number.isNaN(updated)) return "—"

      const diffSec = Math.max(0, Math.floor((this.nowTick - updated) / 1000))

      if (diffSec < 5) return "just now"
      if (diffSec < 60) return `${diffSec}s ago`

      const diffMin = Math.floor(diffSec / 60)
      if (diffMin < 60) return `${diffMin}m ago`

      const diffHr = Math.floor(diffMin / 60)
      return `${diffHr}h ago`
    }
  },
  methods: {
    _startUpdatedTimer() {
      if (this._nowTimer) return
      this._nowTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 1000)
    },
    _stopUpdatedTimer() {
      if (!this._nowTimer) return
      clearInterval(this._nowTimer)
      this._nowTimer = null
    },

    // Called by parent via ref after a task is successfully completed
    removeTask(id) {
      if (!id) return
      const match = (item) => item.id !== id && item.task_id !== id
      this.loanOpen = this.loanOpen.filter(match)
      this.loanDueToday = this.loanDueToday.filter(match)
      this.loanPastDue = this.loanPastDue.filter(match)
      this.borrowerPending = this.borrowerPending.filter(match)
      this.borrowerRequested = this.borrowerRequested.filter(match)
    },

    cleanText(v) {
      if (v == null) return ""
      const s = String(v).trim()
      if (!s) return ""
      const low = s.toLowerCase()
      if (low === "undefined" || low === "null") return ""
      return s
    },
    getLoanNumber(item) {
      return this.cleanText(item?.loannumber || item?.f364 || "")
    },
    getBorrowerName(item) {
      return this.cleanText(item?.borrowername || item?.borrower_name || item?.name || "—") || "—"
    },
    groupByLoanBorrower(items) {
      const map = new Map()

      for (const t of items || []) {
        const loanNumber = this.getLoanNumber(t) || "—"
        const borrower = this.getBorrowerName(t) || "—"
        const key = `${loanNumber}__${borrower}`

        if (!map.has(key)) {
          map.set(key, { loanNumber, borrowers: [borrower], tasks: [] })
        }

        map.get(key).tasks.push(t)
      }

      return Array.from(map.values()).sort((a, b) => {
        const la = String(a.loanNumber || "")
        const lb = String(b.loanNumber || "")
        if (la !== lb) return la.localeCompare(lb)
        const ba = String((a.borrowers || [])[0] || "")
        const bb = String((b.borrowers || [])[0] || "")
        return ba.localeCompare(bb)
      })
    },

    async refresh() {
      if (!this.azureId || !this.accessToken) return

      const reqId = ++this._reqId
      this.isLoading = true
      this.hasError = false
      this.errorMsg = ""

      try {
        await Promise.allSettled([
          this._fetchBorrowerTasks(reqId),
          this._fetchLoanFlow(reqId)
        ])

        if (reqId !== this._reqId) return
        this.lastUpdatedAt = new Date().toISOString()
      } catch (e) {
        if (reqId !== this._reqId) return
        this.hasError = true
        this.errorMsg = e?.response?.data?.data || e?.message || "Failed to load"
      } finally {
        if (reqId === this._reqId) this.isLoading = false
      }
    },

  async _fetchBorrowerTasks(reqId) {
  const res = await apiService.GetSwiftDashboardData(
    this.azureId,
    this.taskSwitchChecked,
    this.taskSwitchChecked,
    this.accessToken
  )

  const raw = res?.data?.data?.data ?? res?.data?.data ?? []
  if (reqId !== this._reqId) return

  const pending = []
  const requested = []

  for (let i = raw.length - 1; i >= 0; i--) {
    const item = raw[i]
    const status = String(item?.status || "").trim().toLowerCase()
    const completed = !!item?.completed
    if (completed) continue

    const next = {
      ...item,
      created: item?.created ? new Date(item.created) : null,
      cust17fv: item?.cust17fv ? new Date(item.cust17fv) : null
    }

    if (status === "pending") {
      pending.unshift(next)
    }

    if (status === "requested") {
      requested.unshift(next)
    }
  }

  this.borrowerPending = pending
  this.borrowerRequested = requested
},

    async _fetchLoanFlow(reqId) {
      const res = this.taskSwitchChecked
        ? await apiService.GetInternalTasksByOrgId(this.azureId, this.accessToken)
        : await apiService.GetInternalTasks(this.azureId, this.accessToken)

      const raw = res?.data?.data?.data ?? res?.data?.data ?? []
      if (reqId !== this._reqId) return

      const dueToday = []
      const pastDue = []
      const open = []

      for (let i = raw.length - 1; i >= 0; i--) {
        const item = raw[i]
        const completed = !!item?.completed
        const status = String(item?.status || "")
        if (completed) continue
        if (status === "Completed") continue

        const next = {
          ...item,
          followupdate: item?.followupdate ? new Date(item.followupdate) : null,
          created: item?.created ? new Date(item.created) : null
        }

        if (isToday(next.followupdate)) dueToday.unshift(next)
        if (isTaskPastDue(next.followupdate)) pastDue.unshift(next)

        if (status === "Pending" || status === "Requested") {
          next.status = "Open"
          open.unshift(next)
        } else if (status === "In Process") {
          open.unshift(next)
        }
      }

      this.loanDueToday = dueToday
      this.loanPastDue = pastDue
      this.loanOpen = open
    },

    openBorrowerTasks() {
      this.$emit("open-grid", {
        title: "Pending Tasks",
        data: this.groupByLoanBorrower(this.borrowerPending),
        columns: this.openTaskColumns,
        sendNotiButton: true,
        dialogType: "borrowerTaskGrouped"
      })
    },

    openRequestedBorrowerTasks() {
      this.$emit("open-grid", {
        title: "Requested Borrower Tasks",
        data: this.groupByLoanBorrower(this.borrowerRequested),
        columns: this.openTaskColumns,
        sendNotiButton: true,
        dialogType: "borrowerTaskGrouped"
      })
    },

    openDueToday() {
      this.$emit("open-grid", {
        title: "Due Today",
        data: this.groupByLoanBorrower(this.loanDueToday),
        columns: this.internalTaskColumns,
        dialogType: "internalTaskGrouped"
      })
    },
    openPastDue() {
      this.$emit("open-grid", {
        title: "Past Due",
        data: this.groupByLoanBorrower(this.loanPastDue),
        columns: this.internalTaskColumns,
        dialogType: "internalTaskGrouped"
      })
    },
    openOpen() {
      this.$emit("open-grid", {
        title: "Open Loan Tasks",
        data: this.groupByLoanBorrower(this.loanOpen),
        columns: this.internalTaskColumns,
        dialogType: "internalTaskGrouped"
      })
    }
  },
  mounted() {
    this._startUpdatedTimer()
    this.refresh()
  },
  beforeUnmount() {
    this._stopUpdatedTimer()
  },
  watch: {
    azureId() {
      this.refresh()
    },
    accessToken() {
      this.refresh()
    },
    taskSwitchChecked() {
      this.refresh()
    }
  }
}
</script>

<style scoped>
.pa-root {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.pa-loading,
.pa-empty {
  min-height: 90px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.65);
}

.pa-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  height: 100%;
  min-height: 0;
}

.pa-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(40, 110, 255, 0.04);
  border-radius: 14px;
  padding: 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 140px;
}

.pa-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.pa-card__title {
  font-weight: 950;
  font-size: 13px;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pa-card__metrics {
  display: grid;
  gap: 10px;
  min-height: 0;
}

.pa-card__metrics--two {
  grid-template-columns: 1fr 1fr;
}

.pa-card__metrics--three {
  grid-template-columns: 1fr 1fr 1fr;
}

.pa-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.65);
  text-align: left;
}

.pa-metric--click {
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
}

.pa-metric--click:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.06);
}

.pa-metric__label {
  font-size: 11px;
  font-weight: 900;
  opacity: 0.75;
}

.pa-metric__value {
  font-size: 22px;
  font-weight: 950;
  line-height: 1.1;
}

.pa-card__footer {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.65);
}

.pa-card__footer-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pa-card__footer-right {
  opacity: 0.85;
}

@media (max-width: 925px) {
  .pa-grid {
    grid-template-columns: 1fr;
  }

  .pa-card__metrics--two,
  .pa-card__metrics--three {
    grid-template-columns: 1fr;
  }
}

.pa-card--borrower {
  background: rgba(40, 110, 255, 0.04);
  border-color: rgba(37, 99, 235, 0.18);
}

.pa-card--loanflow {
  background: rgba(200, 80, 255, 0.06);
  border-color: rgba(168, 85, 247, 0.18);
}

.pa-card__title i {
  font-size: 14px;
  line-height: 1;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.10));
  --fa-secondary-opacity: 1;
}

.pa-card--borrower .pa-card__title i {
  --fa-primary-color: #2563eb;
  --fa-secondary-color: #93c5fd;
}

.pa-card--loanflow .pa-card__title i {
  --fa-primary-color: #a855f7;
  --fa-secondary-color: #f0abfc;
}

.pa-card__footer-left i {
  font-size: 12px;
  --fa-primary-color: rgba(15, 23, 42, 0.75);
  --fa-secondary-color: rgba(15, 23, 42, 0.35);
  --fa-secondary-opacity: 1;
}
</style>
